import { useState, useEffect, useCallback, useMemo } from 'react';
import Spinner from '../components/Spinner';

// Müşteri Proformaları: müşteri (Hilal) BSW'den aldığı ürünleri okutup talep gönderir;
// burada eşleştirilir, TL fiyat girilir, onaylanınca proforma olur. Adetler değiştirilemez,
// bir ürünün adedi birden çok fiyata bölünebilir. Kurallar: database/056_musteri_proforma.sql

const BASE = '/api/musteri-proforma';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

async function indir(path, dosya) {
  const token = localStorage.getItem('ozon_token');
  const r = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error('İndirilemedi');
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = dosya; a.click();
  URL.revokeObjectURL(url);
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('tr-TR', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Para: float yok. Tutarlar 1/10000 TL birimiyle BigInt olarak hesaplanır (sadece ekranda gösterim;
// kesin toplamı onayda DB hesaplar).
const FIYAT = /^\d{1,12}([.,]\d{1,4})?$/;
function birim(metin) {
  const s = String(metin ?? '').trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const eksi = s.startsWith('-');
  const [tam, kesir = ''] = s.replace('-', '').split('.');
  const v = BigInt(tam) * 10000n + BigInt((kesir + '0000').slice(0, 4));
  return eksi ? -v : v;
}
function tl(v) {
  if (v === null || v === undefined) return '—';
  const eksi = v < 0n; const m = eksi ? -v : v;
  const tam = (m / 10000n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  let k = (m % 10000n).toString().padStart(4, '0').replace(/0+$/, '');
  if (k.length < 2) k = k.padEnd(2, '0');
  return `${eksi ? '-' : ''}${tam},${k}`;
}
function metindenTl(metin) { return tl(birim(metin)); }
const noktali = (f) => String(f).trim().replace(',', '.');

const DURUM = {
  bekliyor:   { ad: 'Bekliyor',   cls: 'bg-amber-100 text-amber-800' },
  onaylandi:  { ad: 'Proforma',   cls: 'bg-emerald-100 text-emerald-800' },
  reddedildi: { ad: 'Reddedildi', cls: 'bg-red-100 text-red-700' },
};
const TUR_AD = { urun: 'Ürün', indirim: 'İndirim', hizmet: 'Hizmet bedeli' };

// BSW ürün seçici (eşleştirme)
function UrunSec({ musteri, onunKod, secili, seciliAd, onSecildi }) {
  const [acik, setAcik] = useState(!secili);
  const [q, setQ] = useState('');
  const [sonuc, setSonuc] = useState([]);
  const [hataMsg, setHataMsg] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setSonuc([]); return; }
    const t = setTimeout(() => api('GET', `/urunler?q=${encodeURIComponent(q.trim())}`).then(setSonuc).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  async function sec(u) {
    try {
      const r = await api('PUT', '/esleme', { musteri, onun_offer_id: onunKod, bizim_offer_id: u.offer_id });
      onSecildi(u, r.varsayilan_fiyat);
      setAcik(false); setQ(''); setHataMsg('');
    } catch (e) { setHataMsg(e.message); }
  }
  if (!acik) {
    return (
      <div className="text-xs">
        <div className="font-mono text-slate-800">{secili}</div>
        <div className="text-slate-500 truncate max-w-[220px]">{seciliAd}</div>
        <button onClick={() => setAcik(true)} className="text-indigo-600 hover:underline">değiştir</button>
      </div>
    );
  }
  return (
    <div className="text-xs w-56">
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="BSW kodu / ürün adı ara"
             className="w-full border border-amber-300 rounded px-2 py-1" />
      {hataMsg && <div className="text-red-600 mt-1">{hataMsg}</div>}
      {sonuc.length > 0 && (
        <div className="mt-1 w-72 max-h-48 overflow-auto bg-white border rounded shadow">
          {sonuc.map(u => (
            <button key={u.offer_id} onClick={() => sec(u)} className="block w-full text-left px-2 py-1 hover:bg-indigo-50">
              <span className="font-mono">{u.offer_id}</span> <span className="text-slate-500">{u.name}</span>
            </button>
          ))}
        </div>
      )}
      {secili && <button onClick={() => setAcik(false)} className="text-slate-500 mt-1">vazgeç</button>}
    </div>
  );
}

function TalepDetay({ id, onDegisti }) {
  const [veri, setVeri] = useState(null);
  const [parca, setParca] = useState({});       // onun_offer_id → [{adet, fiyat}]
  const [esleme, setEsleme] = useState({});     // onun_offer_id → {kod, ad}
  const [ekler, setEkler] = useState([]);
  const [zam, setZam] = useState('');
  const [hataMsg, setHataMsg] = useState('');
  const [mesgul, setMesgul] = useState(false);

  const yukle = useCallback(async () => {
    const d = await api('GET', `/talepler/${id}`);
    setVeri(d);
    const p = {}; const e = {};
    d.kalemler.forEach(k => {
      p[k.onun_offer_id] = [{ adet: String(k.adet), fiyat: k.varsayilan_fiyat ? metindenTl(k.varsayilan_fiyat).replace(/\./g, '') : '' }];
      if (k.bizim_offer_id) e[k.onun_offer_id] = { kod: k.bizim_offer_id, ad: k.bizim_ad };
    });
    setParca(p); setEsleme(e); setEkler([]); setZam(''); setHataMsg('');
  }, [id]);
  useEffect(() => { yukle().catch(e => setHataMsg(e.message)); }, [yukle]);

  const hesap = useMemo(() => {
    if (!veri) return null;
    let toplam = 0n; let gecerli = true;
    const kalem = {};
    for (const k of veri.kalemler) {
      const ps = parca[k.onun_offer_id] || [];
      let adet = 0; let tutar = 0n;
      for (const p of ps) {
        const a = Number(p.adet); const f = FIYAT.test(p.fiyat.trim()) ? birim(p.fiyat) : null;
        if (!Number.isInteger(a) || a <= 0 || f === null) gecerli = false;
        else { adet += a; tutar += BigInt(a) * f; }
      }
      if (adet !== k.adet || !esleme[k.onun_offer_id]) gecerli = false;
      kalem[k.onun_offer_id] = { adet, tutar };
      toplam += tutar;
    }
    for (const e of ekler) {
      const t = FIYAT.test(e.tutar.trim()) ? birim(e.tutar) : null;
      if (!t || !e.aciklama.trim()) { gecerli = false; continue; }
      toplam += e.tur === 'indirim' ? -t : t;
    }
    if (toplam < 0n) gecerli = false;
    return { kalem, toplam, gecerli };
  }, [veri, parca, esleme, ekler]);

  if (!veri) return <div className="p-6"><Spinner /> {hataMsg}</div>;
  const { talep, kalemler, proforma } = veri;

  function parcaDegis(kod, i, alan, deger) {
    setParca(s => ({ ...s, [kod]: s[kod].map((p, j) => (j === i ? { ...p, [alan]: deger } : p)) }));
  }
  function parcaEkle(kod, toplamAdet) {
    setParca(s => {
      const mevcut = s[kod].reduce((a, p) => a + (Number(p.adet) || 0), 0);
      return { ...s, [kod]: [...s[kod], { adet: String(Math.max(toplamAdet - mevcut, 0) || ''), fiyat: '' }] };
    });
  }
  function parcaSil(kod, i) { setParca(s => ({ ...s, [kod]: s[kod].filter((_, j) => j !== i) })); }

  function zamUygula() {
    const oran = birim(zam);                       // %, 1/10000 biriminde
    if (oran === null) return;
    setParca(s => {
      const y = {};
      for (const [kod, ps] of Object.entries(s)) {
        y[kod] = ps.map(p => {
          const f = FIYAT.test(p.fiyat.trim()) ? birim(p.fiyat) : null;
          if (f === null) return p;
          // f × (1 + oran/100), 4 ondalığa yuvarlanır
          const yeni = (f * (1000000n + oran) + 500000n) / 1000000n;
          return { ...p, fiyat: tl(yeni).replace(/\./g, '') };
        });
      }
      return y;
    });
  }

  async function onayla() {
    if (!hesap.gecerli) return;
    if (!window.confirm(`${talep.musteri} için ${tl(hesap.toplam)} TL tutarında proforma oluşturulsun mu? Onaydan sonra değiştirilemez.`)) return;
    setMesgul(true); setHataMsg('');
    try {
      await api('POST', `/talepler/${id}/onayla`, {
        urunler: kalemler.map(k => ({
          onun_offer_id: k.onun_offer_id,
          parcalar: parca[k.onun_offer_id].map(p => ({ adet: Number(p.adet), birim_fiyat: noktali(p.fiyat) })),
        })),
        ekler: ekler.map(e => ({ tur: e.tur, aciklama: e.aciklama.trim(), tutar: noktali(e.tutar) })),
      });
      await yukle(); onDegisti();
    } catch (e) { setHataMsg(e.message); }
    setMesgul(false);
  }
  async function reddet() {
    const not = window.prompt('Red sebebi (müşteri görecek):');
    if (!not || !not.trim()) return;
    setMesgul(true);
    try { await api('POST', `/talepler/${id}/reddet`, { not }); await yukle(); onDegisti(); }
    catch (e) { setHataMsg(e.message); }
    setMesgul(false);
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">{talep.musteri} · talep #{talep.uzak_id}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full ${DURUM[talep.durum].cls}`}>{DURUM[talep.durum].ad}</span>
        <span className="text-xs text-slate-500">geldi {formatDate(talep.geldi_at)}</span>
        {talep.not_metni && <span className="text-xs text-slate-600">“{talep.not_metni}”</span>}
      </div>
      {hataMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{hataMsg}</div>}
      {talep.durum === 'reddedildi' && <div className="text-sm text-red-700">Reddedildi: {talep.red_notu}</div>}

      {proforma && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold">{proforma.no}</span>
            <span className="text-sm text-slate-500">{formatDate(proforma.created_at)} · {proforma.created_by}</span>
            <button onClick={() => indir(`/proformalar/${proforma.id}/pdf`, `${proforma.no}.pdf`).catch(e => setHataMsg(e.message))}
                    className="text-sm px-3 py-1 rounded bg-slate-800 text-white">PDF indir</button>
            <button onClick={() => indir(`/proformalar/${proforma.id}/excel`, `${proforma.no}.xlsx`).catch(e => setHataMsg(e.message))}
                    className="text-sm px-3 py-1 rounded bg-emerald-700 text-white">Excel indir</button>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b">
              <th className="py-1">#</th><th>Tür</th><th>Müşteri kodu</th><th>BSW kodu</th><th>Açıklama</th>
              <th className="text-right">Adet</th><th className="text-right">Birim fiyat</th><th className="text-right">Tutar</th>
            </tr></thead>
            <tbody>
              {proforma.satirlar.map(s => (
                <tr key={s.sira} className="border-b border-slate-100">
                  <td className="py-1">{s.sira}</td><td>{TUR_AD[s.tur]}</td>
                  <td className="font-mono text-xs">{s.onun_offer_id}</td><td className="font-mono text-xs">{s.bizim_offer_id}</td>
                  <td>{s.aciklama}</td><td className="text-right">{s.adet}</td>
                  <td className="text-right">{metindenTl(s.birim_fiyat)}</td><td className="text-right">{metindenTl(s.tutar)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={7} className="text-right font-semibold py-2">Toplam</td>
              <td className="text-right font-semibold">{metindenTl(proforma.toplam)} TL</td></tr></tfoot>
          </table>
          </div>
        </div>
      )}

      {talep.durum === 'bekliyor' && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-600">Tüm fiyatlara</span>
            <input value={zam} onChange={e => setZam(e.target.value)} placeholder="10" className="w-16 border rounded px-2 py-1" />
            <span className="text-slate-600">% zam</span>
            <button onClick={zamUygula} className="px-2 py-1 rounded border">uygula</button>
            <span className="text-xs text-slate-400">(eksi yazarsan indirim olur; sadece girilmiş fiyatlara uygulanır)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b">
                <th className="py-1">Müşteri ürünü</th><th className="text-right">Adet</th><th>BSW ürünü</th>
                <th>Fiyat bölümleri (adet × TL)</th><th className="text-right">Tutar</th>
              </tr></thead>
              <tbody>
                {kalemler.map(k => {
                  const h = hesap.kalem[k.onun_offer_id];
                  const eksik = k.adet - h.adet;
                  return (
                    <tr key={k.onun_offer_id} className="border-b border-slate-100 align-top">
                      <td className="py-2"><div className="font-mono text-xs">{k.onun_offer_id}</div>
                        <div className="text-xs text-slate-500 max-w-[220px]">{k.ad}</div></td>
                      <td className="text-right font-semibold py-2">{k.adet}</td>
                      <td className="py-2 px-2">
                        <UrunSec musteri={talep.musteri} onunKod={k.onun_offer_id}
                                 secili={esleme[k.onun_offer_id]?.kod} seciliAd={esleme[k.onun_offer_id]?.ad}
                                 onSecildi={(u, vf) => {
                                   setEsleme(s => ({ ...s, [k.onun_offer_id]: { kod: u.offer_id, ad: u.name } }));
                                   if (vf) setParca(s => ({ ...s, [k.onun_offer_id]: s[k.onun_offer_id].map(p => (p.fiyat ? p : { ...p, fiyat: metindenTl(vf).replace(/\./g, '') })) }));
                                 }} />
                      </td>
                      <td className="py-2 space-y-1">
                        {parca[k.onun_offer_id].map((p, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <input value={p.adet} onChange={e => parcaDegis(k.onun_offer_id, i, 'adet', e.target.value)}
                                   className="w-14 border rounded px-1 py-0.5 text-right" />
                            <span className="text-slate-400">×</span>
                            <input value={p.fiyat} onChange={e => parcaDegis(k.onun_offer_id, i, 'fiyat', e.target.value)}
                                   placeholder="fiyat"
                                   className={`w-24 border rounded px-1 py-0.5 text-right ${p.fiyat && !FIYAT.test(p.fiyat.trim()) ? 'border-red-400' : ''}`} />
                            <span className="text-xs text-slate-400">TL</span>
                            {parca[k.onun_offer_id].length > 1 &&
                              <button onClick={() => parcaSil(k.onun_offer_id, i)} className="text-red-500 text-xs px-1">sil</button>}
                          </div>
                        ))}
                        <div className="flex items-center gap-2 text-xs">
                          <button onClick={() => parcaEkle(k.onun_offer_id, k.adet)} className="text-indigo-600 hover:underline">+ farklı fiyatla böl</button>
                          {eksik !== 0 && <span className="text-red-600">{eksik > 0 ? `${eksik} adet eksik` : `${-eksik} adet fazla`}</span>}
                        </div>
                      </td>
                      <td className="text-right py-2">{tl(h.tutar)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-1">
            {ekler.map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                <select value={e.tur} onChange={ev => setEkler(s => s.map((x, j) => (j === i ? { ...x, tur: ev.target.value } : x)))}
                        className="border rounded px-1 py-0.5">
                  <option value="indirim">İndirim (−)</option>
                  <option value="hizmet">Hizmet bedeli (+)</option>
                </select>
                <input value={e.aciklama} onChange={ev => setEkler(s => s.map((x, j) => (j === i ? { ...x, aciklama: ev.target.value } : x)))}
                       placeholder="açıklama" className="w-64 border rounded px-2 py-0.5" />
                <input value={e.tutar} onChange={ev => setEkler(s => s.map((x, j) => (j === i ? { ...x, tutar: ev.target.value } : x)))}
                       placeholder="tutar" className="w-24 border rounded px-1 py-0.5 text-right" />
                <span className="text-xs text-slate-400">TL</span>
                <button onClick={() => setEkler(s => s.filter((_, j) => j !== i))} className="text-red-500 text-xs">sil</button>
              </div>
            ))}
            <div className="flex gap-3 text-xs">
              <button onClick={() => setEkler(s => [...s, { tur: 'indirim', aciklama: '', tutar: '' }])} className="text-indigo-600 hover:underline">+ indirim</button>
              <button onClick={() => setEkler(s => [...s, { tur: 'hizmet', aciklama: '', tutar: '' }])} className="text-indigo-600 hover:underline">+ hizmet bedeli</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-3">
            <span className="text-sm text-slate-600">Toplam</span>
            <span className={`text-lg font-semibold ${hesap.toplam < 0n ? 'text-red-600' : ''}`}>{tl(hesap.toplam)} TL</span>
            <button onClick={reddet} disabled={mesgul} className="px-3 py-1.5 rounded border border-red-300 text-red-700 text-sm">Reddet</button>
            <button onClick={onayla} disabled={!hesap.gecerli || mesgul}
                    title={hesap.gecerli ? '' : 'Her ürün eşleşmeli, fiyatlar girilmeli ve bölümlerin adedi talep adedine eşit olmalı'}
                    className="px-4 py-1.5 rounded bg-indigo-600 text-white text-sm disabled:opacity-40">Onayla → proforma</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function MusteriProforma() {
  const [liste, setListe] = useState(null);
  const [secili, setSecili] = useState(null);
  const [hataMsg, setHataMsg] = useState('');

  const yukle = useCallback(() => api('GET', '/talepler').then(setListe).catch(e => setHataMsg(e.message)), []);
  useEffect(() => { yukle(); }, [yukle]);

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-semibold mb-1">Müşteri Proformaları</h1>
      <p className="text-sm text-slate-500 mb-4">Müşterilerin BSW'den aldığı ve kendi sisteminde okuttuğu ürünler. Fiyatı gir, onayla; proforma müşterinin sistemine düşer.</p>
      {hataMsg && <div className="text-sm text-red-700 mb-3">{hataMsg}</div>}
      {!liste ? <Spinner /> : (
        <div className="grid md:grid-cols-[320px_1fr] gap-4">
          <div className="bg-white rounded-xl border divide-y max-h-[75vh] overflow-auto">
            {liste.length === 0 && <div className="p-4 text-sm text-slate-500">Henüz talep yok.</div>}
            {liste.map(t => (
              <button key={t.id} onClick={() => setSecili(t.id)}
                      className={`block w-full text-left p-3 hover:bg-slate-50 ${secili === t.id ? 'bg-indigo-50' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{t.musteri} #{t.uzak_id}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${DURUM[t.durum].cls}`}>{DURUM[t.durum].ad}</span>
                </div>
                <div className="text-xs text-slate-500">{formatDate(t.geldi_at)} · {t.kalem} ürün · {t.adet} adet</div>
                {t.proforma_no && <div className="text-xs text-emerald-700">{t.proforma_no} · {metindenTl(t.toplam)} TL</div>}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-xl border min-h-[200px] min-w-0">
            {secili ? <TalepDetay key={secili} id={secili} onDegisti={yukle} />
                    : <div className="p-6 text-sm text-slate-500">Soldan bir talep seç.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
